// src/components/GalleryPage.tsx

import React, { useState, useEffect } from "react";
import { Button } from "./ui/button.tsx";
import { 
  ExternalLink, 
  Image as ImageIcon, 
  Camera, 
  Lock 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

// Folder interface (backend response)
interface Folder {
  id: number;
  name: string;
  description?: string;
  visibility: "public" | "registered";
  created_at: string;
  cover_image?: string;
  image_count: number; // Backend'den gelen görsel sayısı
}

export function GalleryPage() {
  const navigate = useNavigate();

  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const token = localStorage.getItem("access");
  const isLoggedIn = Boolean(token);

  // Placeholder cover image
  const PLACEHOLDER_IMAGE =
    "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&auto=format&fit=crop&q=60";

  useEffect(() => {
    const fetchFolders = async () => {
      setLoading(true);
      setErrorMsg(null);

      try {
        const res = await axios.get(
          "https://sdal-project-backend.onrender.com/api/gallery/folders/",
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );

        const rawFolders = Array.isArray(res.data) ? res.data : res.data.results || [];
        setFolders(Array.isArray(rawFolders) ? rawFolders : []);
      } catch (error: any) {
        console.error("❌ Gallery folders could not be loaded:", error);
        setErrorMsg("Albümler yüklenirken bir sorun oluştu.");
      } finally {
        setLoading(false);
      }
    };

    fetchFolders();
  }, [token]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
        <div className="w-12 h-12 border-4 border-slate-100 border-t-[#A8322D] rounded-full animate-spin" />
        <p className="text-slate-500 font-medium tracking-wide animate-pulse">
          Albümler yükleniyor...
        </p>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
        <p className="text-red-500 font-medium">{errorMsg}</p>
        <Button onClick={() => window.location.reload()} variant="outline">
          Sayfayı Yenile
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-8 pb-24 max-w-7xl space-y-10">
      
      {/* --- HEADER & CTA SECTION --- */}
      <div className="flex flex-col gap-6">
        {isLoggedIn && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-[#A8322D] to-[#8a2723] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden shadow-lg shadow-[#A8322D]/20"
          >
            <div className="absolute right-0 top-0 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none -translate-y-1/2 translate-x-1/3" />
            <div className="relative z-10 flex items-start gap-4">
              <div className="bg-white/15 p-3 rounded-xl shrink-0 mt-1 sm:mt-0 backdrop-blur-sm border border-white/10">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white text-lg font-bold mb-1">
                  Arşivinizi Bizimle Paylaşın
                </h3>
                <p className="text-white/80 text-sm max-w-2xl leading-relaxed">
                  Elinizdeki eski fotoğrafları Drive klasörümüze yükleyerek, mesleki arşivimizin büyümesine katkı sağlayabilirsiniz.
                </p>
              </div>
            </div>
            <Button
              onClick={() => window.open("https://drive.google.com", "_blank")}
              className="bg-white text-[#A8322D] hover:bg-orange-50 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl px-6 py-5 text-sm font-bold whitespace-nowrap shrink-0 relative z-10"
            >
              Drive'a Yükle
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </motion.div>
        )}
      </div>

      {/* --- EMPTY STATE --- */}
      {folders.length === 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center py-20 bg-slate-50 border border-dashed border-slate-200 rounded-2xl"
        >
          <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-slate-100">
            <ImageIcon className="h-8 w-8 text-slate-300" />
          </div>
          <h3 className="text-lg font-bold text-slate-800 mb-1">Henüz Albüm Yok</h3>
          <p className="text-sm text-slate-500 max-w-sm mx-auto">
            Yakında buraya yeni etkinlik albümleri eklenecektir.
          </p>
        </motion.div>
      )}

      {/* --- FOLDER LIST --- */}
      {folders.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {folders.map((folder, index) => (
            <motion.div
              key={folder.id}
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.4, delay: index * 0.1 }}
              onClick={() => navigate(`/gallery/folders/${folder.id}`)}
              className="group flex flex-col bg-white border border-slate-200/60 rounded-2xl p-3 cursor-pointer hover:shadow-xl hover:shadow-[#A8322D]/5 hover:border-[#A8322D]/20 transition-all duration-300"
            >
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 mb-4">
                <img
                  src={folder.cover_image || PLACEHOLDER_IMAGE}
                  alt={folder.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
                
                {folder.visibility === "registered" && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md text-white text-xs font-medium">
                    <Lock className="w-3 h-3" /> Özel
                  </div>
                )}
              </div>

              {/* İsim ve Görsel Sayısı */}
              <div className="flex flex-col px-1 pb-2">
                <h3 className="font-bold text-lg text-slate-800 line-clamp-1 group-hover:text-[#A8322D] transition-colors mt-2">
                  {folder.name}
                </h3>
                <div className="text-xs text-slate-400 mt-1 font-medium">
                  {folder.image_count} Görsel
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}